/**
 * EVENT BINDING — главная инициализация DOM-обработчиков.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: EVENT BINDING).
 *
 *   - bindEvents() — навешивает обработчики на:
 *     · search input + category filter buttons
 *     · pricing inputs (hidden-markup, partner-commission)
 *     · client info inputs (pax-count, tour-start, tour-end, name/phone/email)
 *     · global header buttons (mode-quote / mode-checklist / my-estimates)
 *     · file menu items
 *     · estimates-modal tabs
 *
 * @module app/event-binding
 */

// i18n helper: returns translation if loaded, else fallback (EN). Safe to call before module loads.
const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет bindEvents() к prototype класса.
 * Метод вызывается из init() (один раз при старте).
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachEventBinding(ClassRef) {
    Object.assign(ClassRef.prototype, {
        bindEvents() {
            // Search with debouncing
            document.getElementById('search').addEventListener('input', (e) => {
                this.debouncedSearch(e.target.value);
            });

            // Category filters
            document.querySelectorAll('.category-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    document.querySelectorAll('.category-btn').forEach(b => b.classList.remove('active'));
                    e.target.classList.add('active');
                    this.state.activeCategory = e.target.dataset.category;
                    this.renderTemplates();
                });
            });

            // Phase 8c: client-currency-select — populate options from cache + bind change.
            const clientCurSel = document.getElementById('client-currency-select');
            if (clientCurSel) {
                // Populate from cache (если уже загружен) или дефолтно [USD]
                const cache = this._currencyCache;
                if (cache && cache.activeCurrencies && cache.activeCurrencies.length) {
                    const cur = this.state.clientCurrency || cache.defaultClient || 'USD';
                    clientCurSel.innerHTML = cache.activeCurrencies.map(c =>
                        `<option value="${c.code}" ${c.code === cur ? 'selected' : ''}>${c.code} (${c.symbol})</option>`
                    ).join('');
                } else {
                    clientCurSel.innerHTML = '<option value="USD" selected>USD ($)</option>';
                }
                clientCurSel.addEventListener('change', (e) => {
                    this.state.clientCurrency = e.target.value;
                    if (typeof this.scheduleUpdate === 'function') this.scheduleUpdate();
                    if (typeof this.markQuoteAsUnsaved === 'function') this.markQuoteAsUnsaved();
                });
            }

            // Sprint 18: tour-name handler (user-friendly identifier)
            const tourNameInput = document.getElementById('tour-name');
            if (tourNameInput) {
                tourNameInput.value = this.state.tourName || '';
                tourNameInput.addEventListener('input', (e) => {
                    this.state.tourName = e.target.value;
                    if (typeof this.scheduleUpdate === 'function') this.scheduleUpdate();
                    if (typeof this.markQuoteAsUnsaved === 'function') this.markQuoteAsUnsaved();
                });
            }

            // Sprint 18: client-name typeahead — поиск клиентов с auto-translit
            const clientNameInput = document.getElementById('client-name');
            const suggestBox = document.getElementById('client-name-suggestions');
            const clientIdHidden = document.getElementById('client-id');
            if (clientNameInput && suggestBox && window.apiClient) {
                let searchTimer = null;
                const showSuggestions = (clients) => {
                    if (!clients || clients.length === 0) {
                        suggestBox.style.display = 'none';
                        suggestBox.innerHTML = '';
                        return;
                    }
                    const estimatesLabel = _t('app.event.client_typeahead.estimates_short', 'quotes');
                    const createLabel = _t('app.event.client_typeahead.create_new', '+ Create new client');
                    suggestBox.innerHTML = clients.slice(0, 8).map(c =>
                        `<div class="ct-row" data-id="${c.id}" data-name="${(c.name||'').replace(/"/g,'&quot;')}" data-email="${(c.email||'').replace(/"/g,'&quot;')}" data-phone="${(c.phone||'').replace(/"/g,'&quot;')}" style="padding:8px 12px;cursor:pointer;border-bottom:1px solid var(--gray-100);"><div style="font-weight:500;">${c.name||'—'}</div><div style="color:var(--gray-500);font-size:11px;">${[c.email,c.phone,c.estimates_count?(c.estimates_count+' '+estimatesLabel):null].filter(Boolean).join(' • ')}</div></div>`
                    ).join('') + `<div class="ct-row ct-create" style="padding:8px 12px;cursor:pointer;background:var(--gray-50);color:var(--primary-600);font-weight:500;">${createLabel} "${clientNameInput.value}"</div>`;
                    suggestBox.style.display = 'block';
                };
                const fetchSuggestions = async (q) => {
                    if (!q || q.trim().length < 2) {
                        suggestBox.style.display = 'none';
                        return;
                    }
                    try {
                        const r = await fetch('/api/v1/clients?q=' + encodeURIComponent(q.trim()) + '&limit=10', { credentials: 'include' });
                        if (!r.ok) return;
                        const d = await r.json();
                        showSuggestions(d.data?.clients || []);
                    } catch (err) {
                        console.warn('[ClientTypeahead] search failed:', err);
                    }
                };
                clientNameInput.addEventListener('input', (e) => {
                    this.state.clientName = e.target.value;
                    // Сброс client_id при ручном изменении (если не выбрали из выпадашки)
                    if (clientIdHidden) clientIdHidden.value = '';
                    this.state.clientId = null;
                    if (searchTimer) clearTimeout(searchTimer);
                    searchTimer = setTimeout(() => fetchSuggestions(e.target.value), 250);
                    if (typeof this.scheduleUpdate === 'function') this.scheduleUpdate();
                    if (typeof this.markQuoteAsUnsaved === 'function') this.markQuoteAsUnsaved();
                });
                suggestBox.addEventListener('click', async (e) => {
                    const row = e.target.closest('.ct-row');
                    if (!row) return;
                    if (row.classList.contains('ct-create')) {
                        // Создать нового клиента inline
                        const name = clientNameInput.value.trim();
                        if (!name) return;
                        try {
                            const r = await fetch('/api/v1/clients', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify({ name, email: this.state.clientEmail || null, phone: this.state.clientPhone || null })
                            });
                            const d = await r.json();
                            if (d.success && d.data?.id) {
                                this.state.clientId = d.data.id;
                                if (clientIdHidden) clientIdHidden.value = d.data.id;
                                console.log('[ClientTypeahead] created client', d.data.id);
                                if (typeof this.showNotification === 'function') {
                                    this.showNotification(_t('app.event.client_typeahead.client_created', 'Client "{name}" created').replace('{name}', name));
                                }
                            }
                        } catch (err) {
                            console.warn('[ClientTypeahead] create failed:', err);
                        }
                    } else {
                        // Выбрать существующего
                        const cid = row.dataset.id;
                        this.state.clientId = cid;
                        this.state.clientName = row.dataset.name || '';
                        if (row.dataset.email && !this.state.clientEmail) {
                            this.state.clientEmail = row.dataset.email;
                            const emailInp = document.getElementById('client-email');
                            if (emailInp) emailInp.value = row.dataset.email;
                        }
                        if (row.dataset.phone && !this.state.clientPhone) {
                            this.state.clientPhone = row.dataset.phone;
                            const phoneInp = document.getElementById('client-phone');
                            if (phoneInp) phoneInp.value = row.dataset.phone;
                        }
                        clientNameInput.value = row.dataset.name || '';
                        if (clientIdHidden) clientIdHidden.value = cid;
                    }
                    suggestBox.style.display = 'none';
                    if (typeof this.markQuoteAsUnsaved === 'function') this.markQuoteAsUnsaved();
                });
                // Скрыть при клике вне
                document.addEventListener('click', (e) => {
                    if (!suggestBox.contains(e.target) && e.target !== clientNameInput) {
                        suggestBox.style.display = 'none';
                    }
                });
            }

            // Settings with validation
            ['pax-count', 'tour-start', 'tour-end', 'client-name', 'client-phone', 'client-email', 'quote-description', 'program-description', 'booking-terms'].forEach(id => {
                const element = document.getElementById(id);
                element.addEventListener('change', (e) => {
                    const field = id.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());

                    if (field === 'paxCount') {
                        const value = Math.max(1, parseInt(e.target.value) || 1);
                        this.state.paxCount = value;
                        e.target.value = value;
                    } else if (field === 'tourStart' || field === 'tourEnd') {
                        this.state[field] = e.target.value;
                        // При изменении любой даты проверяем весь диапазон
                        setTimeout(() => this.validateDateRange(), 0);
                        // Отмечаем смету как несохраненную
                        this.markQuoteAsUnsaved();
                        // Обновляем отображение календарных дат в списке услуг
                        if (this.state.services.length > 0) {
                            this.renderServices();
                            this.renderServicesDetailList();
                        }
                    } else {
                        this.state[field] = e.target.value;
                    }

                    this.scheduleUpdate();
                });

                // Real-time validation feedback
                element.addEventListener('blur', () => {
                    this.validateField(id, element.value);
                });

                // Автоматическое обновление высоты для textarea в коллапсируемых секциях
                if (id === 'quote-description' || id === 'program-description') {
                    element.addEventListener('input', () => {
                        this.updateCollapsibleHeight(id);
                    });
                }
            });

            // Hidden markup input (without slider)
            const hiddenMarkupInput = document.getElementById('hidden-markup');

            hiddenMarkupInput.addEventListener('input', (e) => {
                // Разрешаем только цифры, запятую и точку (один разделитель)
                let inputValue = e.target.value;

                // Удаляем все символы кроме цифр, точки и запятой
                inputValue = inputValue.replace(/[^\d.,]/g, '');

                // Разрешаем только один разделитель
                const separatorCount = (inputValue.match(/[.,]/g) || []).length;
                if (separatorCount > 1) {
                    // Оставляем только первый разделитель
                    const firstSeparatorIndex = inputValue.search(/[.,]/);
                    inputValue = inputValue.substring(0, firstSeparatorIndex + 1) +
                                inputValue.substring(firstSeparatorIndex + 1).replace(/[.,]/g, '');
                }

                // Заменяем точку на запятую
                inputValue = inputValue.replace('.', ',');

                // Обновляем поле без форматирования (чтобы можно было вводить)
                e.target.value = inputValue;

                // Парсим для state и валидации
                const parsedValue = parseFloat(inputValue.replace(',', '.'));
                if (!isNaN(parsedValue)) {
                    const value = Math.max(0, Math.min(100, parsedValue));
                    this.state.hiddenMarkup = value;
                    this.debouncedUpdate();
                }
            });

            hiddenMarkupInput.addEventListener('blur', (e) => {
                // При потере фокуса - форматируем значение
                const parsedValue = parseFloat(e.target.value.replace(',', '.'));
                if (!isNaN(parsedValue)) {
                    const value = Math.max(0, Math.min(100, parsedValue));
                    this.state.hiddenMarkup = value;
                    e.target.value = value.toFixed(1).replace('.', ',');
                } else {
                    // Если пустое или невалидное - ставим 0
                    this.state.hiddenMarkup = 0;
                    e.target.value = '0,0';
                }
                this.debouncedUpdate();
            });

            // Partner commission input (without slider)
            const partnerCommissionInput = document.getElementById('partner-commission');

            partnerCommissionInput.addEventListener('input', (e) => {
                // Разрешаем только цифры, запятую и точку (один разделитель)
                let inputValue = e.target.value;

                // Удаляем все символы кроме цифр, точки и запятой
                inputValue = inputValue.replace(/[^\d.,]/g, '');

                // Разрешаем только один разделитель
                const separatorCount = (inputValue.match(/[.,]/g) || []).length;
                if (separatorCount > 1) {
                    // Оставляем только первый разделитель
                    const firstSeparatorIndex = inputValue.search(/[.,]/);
                    inputValue = inputValue.substring(0, firstSeparatorIndex + 1) +
                                inputValue.substring(firstSeparatorIndex + 1).replace(/[.,]/g, '');
                }

                // Заменяем точку на запятую
                inputValue = inputValue.replace('.', ',');

                // Обновляем поле без форматирования (чтобы можно было вводить)
                e.target.value = inputValue;

                // Парсим для state и валидации
                const parsedValue = parseFloat(inputValue.replace(',', '.'));
                if (!isNaN(parsedValue)) {
                    const value = Math.max(0, Math.min(100, parsedValue));
                    this.state.partnerCommission = value;
                    this.debouncedUpdate();
                }
            });

            partnerCommissionInput.addEventListener('blur', (e) => {
                // При потере фокуса - форматируем значение
                const parsedValue = parseFloat(e.target.value.replace(',', '.'));
                if (!isNaN(parsedValue)) {
                    const value = Math.max(0, Math.min(100, parsedValue));
                    this.state.partnerCommission = value;
                    e.target.value = value.toFixed(1).replace('.', ',');
                } else {
                    // Если пустое или невалидное - ставим 0
                    this.state.partnerCommission = 0;
                    e.target.value = '0,0';
                }
                this.debouncedUpdate();
            });

            // Sprint 23 W7: partner select dropdown — populates options + auto-apply default %
            const partnerSelect = document.getElementById('partner-commission-id');
            if (partnerSelect) {
                // Load partners list (only is_referrer=1, active)
                fetch('/api/v1/partners?status=active', { credentials: 'include' })
                    .then(r => r.json())
                    .then(d => {
                        const partners = d?.data?.partners || [];
                        for (const p of partners) {
                            const name = p.contact_name || p.company_name || _t('app.event.partner.unnamed', 'No name');
                            const isTripster = name.toLowerCase() === 'tripster';
                            const opt = document.createElement('option');
                            opt.value = p.id;
                            const defaultSuffix = p.default_commission_pct
                                ? ' ' + _t('app.event.partner.default_pct', '({pct}% by default)').replace('{pct}', p.default_commission_pct)
                                : '';
                            opt.textContent = (isTripster ? '🌐 ' : '') + name + defaultSuffix;
                            opt.dataset.defaultPct = String(p.default_commission_pct || 0);
                            partnerSelect.appendChild(opt);
                        }
                        // Restore previously-selected partner if estimate already has one
                        if (this.state.partnerCommissionId) {
                            partnerSelect.value = this.state.partnerCommissionId;
                        }
                    })
                    .catch(err => console.warn('[partner-select] load failed:', err));

                partnerSelect.addEventListener('change', (e) => {
                    const selected = e.target.value || null;
                    this.state.partnerCommissionId = selected;
                    // Auto-fill default % if % currently 0 and partner has a default
                    if (selected) {
                        const opt = e.target.selectedOptions[0];
                        const defPct = parseFloat(opt?.dataset?.defaultPct || '0');
                        if (defPct > 0 && (!this.state.partnerCommission || this.state.partnerCommission === 0)) {
                            this.state.partnerCommission = defPct;
                            partnerCommissionInput.value = defPct.toFixed(1).replace('.', ',');
                        }
                    }
                    this.debouncedUpdate();
                });
            }

            // Sprint 19a: prepayment manual amount
            const prepaymentManualInput = document.getElementById('prepayment-manual');
            if (prepaymentManualInput) {
                const updatePrepaymentManual = (e) => {
                    const raw = String(e.target.value || '').replace(/[^\d.,]/g, '').replace(',', '.');
                    const parsed = parseFloat(raw);
                    this.state.prepaymentManual = !isNaN(parsed) && parsed >= 0 ? parsed : 0;
                    this.debouncedUpdate();
                };
                prepaymentManualInput.addEventListener('input', updatePrepaymentManual);
                prepaymentManualInput.addEventListener('blur', (e) => {
                    updatePrepaymentManual(e);
                    e.target.value = (this.state.prepaymentManual || 0).toString();
                });
            }

            // Sprint 19a: approval checkboxes (incoming client payments)
            const approvalPrepayment = document.getElementById('approval-prepayment');
            const approvalFinal = document.getElementById('approval-final-payment');
            const handleApproval = async (type, checkbox, metaEl) => {
                if (!this.state.currentQuoteId) return;
                const wasChecked = checkbox.checked;
                try {
                    const r = await window.apiClient._fetchWithAuth(
                        `/api/v1/estimates/${this.state.currentQuoteId}/approve-payment`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ type, approved: wasChecked })
                        }
                    );
                    const json = await r.json();
                    if (!r.ok || !json.success) throw new Error(json.error || `HTTP ${r.status}`);
                    if (type === 'prepayment') {
                        this.state.prepaymentApprovedAt = json.data.prepayment_approved_at;
                        this.state.prepaymentApprovedBy = json.data.prepayment_approved_by;
                    } else {
                        this.state.finalPaymentApprovedAt = json.data.final_payment_approved_at;
                        this.state.finalPaymentApprovedBy = json.data.final_payment_approved_by;
                    }
                    if (metaEl) {
                        const ts = type === 'prepayment' ? json.data.prepayment_approved_at : json.data.final_payment_approved_at;
                        const by = type === 'prepayment' ? json.data.prepayment_approved_by : json.data.final_payment_approved_by;
                        metaEl.textContent = ts ? `${by} · ${new Date(ts * 1000).toLocaleDateString('ru-RU')}` : '';
                    }
                    // Re-render lifecycle bar so the in_progress block-state updates
                    if (type === 'prepayment' && this.state.currentQuoteId) {
                        try {
                            const fresh = await window.apiClient.getEstimate(this.state.currentQuoteId);
                            const container = document.getElementById('estimate-lifecycle-bar-container');
                            if (container && window.EstimateStatusBar && fresh) {
                                container.innerHTML = '';
                                const newBar = window.EstimateStatusBar.render({
                                    status: fresh.status,
                                    allowedTransitions: fresh.allowedTransitions || [],
                                    nextStatus: fresh.nextStatus,
                                    reverseStatus: fresh.reverseStatus,
                                    prepaymentApprovedAt: fresh.prepaymentApprovedAt
                                });
                                container.appendChild(newBar);
                                window.EstimateStatusBar.bind(this.state.currentQuoteId, window.apiClient, () => {});
                            }
                        } catch (_) { /* non-fatal — UI re-render is best-effort */ }
                    }
                } catch (err) {
                    console.error('approval error:', err);
                    checkbox.checked = !wasChecked;
                    const msg = _t('common.error', 'Error') + ': ' + err.message;
                    if (typeof this.showNotification === 'function') {
                        this.showNotification(msg, true);
                    } else {
                        alert(msg);
                    }
                }
            };
            if (approvalPrepayment) {
                approvalPrepayment.addEventListener('change', () => handleApproval(
                    'prepayment', approvalPrepayment, document.getElementById('approval-prepayment-meta')
                ));
            }
            if (approvalFinal) {
                approvalFinal.addEventListener('change', () => handleApproval(
                    'final', approvalFinal, document.getElementById('approval-final-payment-meta')
                ));
            }

            // Calendar sync: when tour-start is selected, set tour-end month to match
            const tourStartInput = document.getElementById('tour-start');
            const tourEndInput = document.getElementById('tour-end');

            if (tourStartInput && tourEndInput) {
                tourStartInput.addEventListener('input', (e) => {
                    if (e.target.value) {
                        // Установить месяц для tour-end соответствующий tour-start
                        const startDate = new Date(e.target.value);
                        if (!isNaN(startDate.getTime())) {
                            // Если tour-end пустой или раньше начала, установить на тот же месяц
                            if (!tourEndInput.value || new Date(tourEndInput.value) < startDate) {
                                const year = startDate.getFullYear();
                                const month = String(startDate.getMonth() + 1).padStart(2, '0');
                                tourEndInput.setAttribute('min', e.target.value);
                                // Открыть date picker на том же месяце (браузер поддержка)
                                if (tourEndInput.showPicker) {
                                    setTimeout(() => {
                                        tourEndInput.focus();
                                    }, 100);
                                }
                            }
                        }
                    }
                });
            }

            // ✅ Event handler для кнопки "История версий" (Этап 4.3)
            const restoreBackupBtn = document.getElementById('restore-from-backup-btn');
            if (restoreBackupBtn) {
                restoreBackupBtn.addEventListener('click', () => {
                    this.showBackupsList();
                });
            }
        },
    });
}
